import { MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { IChartApi, LogicalRange } from 'lightweight-charts';

export function useSyncedCharts({
  mainChartRef,
  extraChartRef,
  pauseRef,
  enabled,
  deps = [],
}: {
  mainChartRef: MutableRefObject<IChartApi | undefined>;
  extraChartRef: MutableRefObject<IChartApi | undefined>;
  pauseRef?: MutableRefObject<boolean>;
  enabled: boolean;
  deps?: any[];
}) {
  const isSyncingRef = useRef(false);
  const epsilon = 1e-2;

  const validateNow = useMemo(
    () => () => {
      const mc = mainChartRef.current;
      const ec = extraChartRef.current;
      if (!mc || !ec) return;
      const mRange = mc.timeScale().getVisibleLogicalRange?.();
      const eRange = ec.timeScale().getVisibleLogicalRange?.();
      if (!mRange || !eRange) return;

      const same =
        Math.abs(mRange.from - eRange.from) < epsilon && Math.abs(mRange.to - eRange.to) < epsilon;
      if (!same) {
        isSyncingRef.current = true;
        ec.timeScale().setVisibleLogicalRange(mRange);
        setTimeout(() => (isSyncingRef.current = false), 0);
      }
    },
    [mainChartRef, extraChartRef],
  );

  useEffect(() => {
    if (!enabled) return;

    const mc = mainChartRef.current;
    const ec = extraChartRef.current;
    if (!mc || !ec) return;

    const guard = () => Boolean(pauseRef?.current);

    const onMainLogical = (range: LogicalRange | null) => {
      if (!range || guard() || isSyncingRef.current) return;
      isSyncingRef.current = true;
      ec.timeScale().setVisibleLogicalRange(range);
      setTimeout(() => (isSyncingRef.current = false), 0);
    };

    const onExtraLogical = (range: LogicalRange | null) => {
      if (!range || guard() || isSyncingRef.current) return;
      isSyncingRef.current = true;
      mc.timeScale().setVisibleLogicalRange(range);
      setTimeout(() => (isSyncingRef.current = false), 0);
    };

    mc.timeScale().subscribeVisibleLogicalRangeChange(onMainLogical);
    ec.timeScale().subscribeVisibleLogicalRangeChange(onExtraLogical);

    validateNow();

    return () => {
      mc.timeScale().unsubscribeVisibleLogicalRangeChange(onMainLogical);
      ec.timeScale().unsubscribeVisibleLogicalRangeChange(onExtraLogical);
    };
  }, [
    enabled,
    mainChartRef,
    extraChartRef,
    pauseRef,
    validateNow,
    mainChartRef.current,
    extraChartRef.current,
    ...deps,
  ]);

  useEffect(() => {
    if (!enabled) return;
    validateNow();
  }, deps);

  return { validateNow };
}
